"use client";
import Image from "next/image";
import { Avatar } from "@mui/material";
import CustomNavLink from "@/components/CustomLink/CustomNavLink";
import getFormattedDate from "@/lib/getFormattedDate";
import usePost from "@/hooks/usePost";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import toast from "react-hot-toast";
import Rating from "react-rating-stars-component";
import { useLoggedIn } from "@/store/useAuthStore";
import { useDictionary } from "@/components/DictProvider/DictProvider";
import useImageUrlModifier from "@/lib/useImageUrlModifier";

export default function ProductReview({ productId }) {
  const [reviewData, setReviewData] = useState({
    productName: "",
    reviews: [],
    averageRating: 0,
    totalRatings: 0,
    ratingCounts: {},
  });

  const dict = useDictionary();
  const { auth } = useLoggedIn();
  const { PostMethod } = usePost();
  const pathName = usePathname();

  const modifyImageUrl = useImageUrlModifier();

  const getData = async (productId) => {
    const res = await PostMethod({
      url: `product/checkPreviousBuy/${productId}`,
      method: "get",
      language: pathName?.split("/")[1],
    });


    if (res?.rating !== null) {
      setReviewData({
        productName: res?.product?.en?.product,
        reviews: res?.rating?.comments,
        averageRating: res?.rating?.rating,
        totalRatings: res?.rating?.totalUsers,
        ratingCounts: res?.rating?.breakdown?.reduce((acc, item) => {
          acc[item?.rating] = item?.count;
          return acc;
        }, {}),
      });
    }
  };

  const upVote = async (reviewID) => {
    if (!auth) {
      toast.error("Please login first!");
    }
    try {
      const res = await PostMethod({
        url: `rating/like/${reviewID}`,
        method: "get",
      });
      if (res?.success) {
        toast.success("Thanks for your voting");
        getData(productId);
      }
    } catch (error) {
      toast.error(error?.message || "Something Went Wrong");
    }
  };
  const downVote = async (reviewID) => {
    if (!auth) {
      toast.error("Please login first!");
    }
    try {
      const res = await PostMethod({
        url: `rating/dislike/${reviewID}`,
        method: "get",
      });
      if (res?.success) {
        toast.success("Thanks for your voting");
        getData(productId);
      }
    } catch (error) {
      toast.error(error?.message || "Something Went Wrong");
    }
  };

  useEffect(() => {
    getData(productId);
    // eslint-disable-next-line
  }, [productId]);

  // Function to round to the nearest 0.5
  const roundToHalf = (value) => {
    if (!value) {
      return 0;
    }
    return Math.round(value * 2) / 2;
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-2xl font-semibold">
          {dict.poductDescriptionPage.ratingsAndReviews}
        </h2>
        <CustomNavLink
          href={`rating-and-review/${productId}`}
          className="text-primary hover:underline font-semibold"
        >
          {dict.poductDescriptionPage.rateProduct}
        </CustomNavLink>
      </div>

      <div className="mb-12 w-full">
        <div className="flex justify-between items-center w-full">
          <div className="flex flex-col items-center justify-center  gap-2 mb-2">
            <p className="text-4xl font-bold">{reviewData?.averageRating}</p>
            <div className="flex gap-0.5">
              {reviewData?.averageRating && (
                <Rating
                  count={5}
                  value={roundToHalf(reviewData?.averageRating)}
                  size={20}
                  activeColor="#14b8a6"
                  isHalf={true} // Allows half-star ratings
                  edit={false}
                />
              )}
            </div>
            <span className="text-gray-500 text-sm">
              ({reviewData?.totalRatings} {dict.poductDescriptionPage.ratings})
            </span>
          </div>

          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((rating) => (
              <div key={rating} className="flex items-center gap-2">
                <span className="w-3">{rating}</span>
                <svg
                  className="w-4 h-4 text-teal-500 fill-teal-500"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <div className="w-[116px] md:w-[228px] h-[4px] md:h-[6px] rounded-full bg-[#D9D9D9] mr-[8px]">
                  <div
                    style={{
                      width: `${((reviewData?.ratingCounts[rating] || 0) /
                          reviewData?.totalRatings) *
                        100
                        }%`,
                    }}
                    className={`bg-[#0DB3A9] rounded-full h-full`}
                  ></div>
                </div>
                <span className="w-6 text-sm text-gray-500">
                  {reviewData?.ratingCounts[rating] || 0}
                </span>
              </div>
            ))}
          </div>
          <div></div>
        </div>
      </div>

      <div className="space-y-8">
        {reviewData?.reviews.map((review) => (
          <div key={review.id} className="border-b pb-8">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <Avatar
                  src={modifyImageUrl(review?.user?.userImage, 100, 100)}
                  alt={`${review?.user?.firstName} ${review?.user?.lastName}`}
                ></Avatar>
                <div>
                  <h3 className="font-medium">{`${review?.user?.firstName} ${review?.user?.lastName}`}</h3>
                  <div className="flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <svg
                        key={i}
                        className={`w-4 h-4 ${i < review?.rating
                            ? "text-teal-500 fill-teal-500"
                            : "text-gray-300 fill-gray-300"
                          }`}
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </div>
              </div>
              <span className="text-sm text-gray-500">
                {getFormattedDate(review?.createdAt)}
              </span>
            </div>

            <p className="text-gray-700 mb-4">
              {dict.poductDescriptionPage.reviewFor}: {reviewData?.productName}
            </p>
            <p className="text-gray-700 mb-4 font-semibold">
              {review?.comments}
            </p>

            {review?.files && (
              <div className="flex gap-4 mb-4">
                {review?.files?.map((image, index) => (
                  <div
                    key={index}
                    className="relative w-24 h-24 border rounded-lg overflow-hidden"
                  >
                    <Image
                      src={modifyImageUrl(image, 200, 200) || ""}
                      alt={`Review image ${index + 1}`}
                      width={96}
                      height={96}
                      className="object-cover"
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-6">
              <button
                onClick={() => {
                  upVote(review?._id);
                }}
                className="flex group items-baseline gap-4 text-sm text-gray-500 hover:text-gray-700"
              >
                <div className="w-4 h-4">
                  <svg
                    width="22"
                    height="21"
                    viewBox="0 0 22 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`${review?.isUpvotedByRequestedUser
                        ? "fill-primary"
                        : "group-hover:fill-primary"
                      }  transition-all duration-300`}
                  >
                    <path
                      d="M13.0245 7.81933L12.8549 8.62684H13.68H20C20.3528 8.62684 20.6911 8.76699 20.9406 9.01645C21.19 9.2659 21.3302 9.60424 21.3302 9.95703V11.957C21.3302 12.1241 21.2994 12.2787 21.2398 12.4339L18.2243 19.4733L18.2243 19.4733L18.2217 19.4794C18.0235 19.9552 17.5538 20.2872 17 20.2872H8C7.64721 20.2872 7.30887 20.1471 7.05941 19.8976C6.80996 19.6482 6.66981 19.3098 6.66981 18.957V8.95703C6.66981 8.59093 6.81475 8.25954 7.06363 8.01066L13.1722 1.90205L13.7564 2.48066C13.7567 2.48097 13.757 2.48129 13.7573 2.4816C13.9067 2.63145 14.0002 2.8428 14.0002 3.06703C14.0002 3.13721 13.9938 3.20178 13.9837 3.25304L13.0245 7.81933ZM3.33019 9.62684V20.2872H0.669811V9.62684H3.33019Z"
                      stroke="black"
                      stroke-opacity="0.4"
                      stroke-width="1.33962"
                      className={`${review?.isUpvotedByRequestedUser
                          ? "stroke-primary"
                          : "group-hover:stroke-primary"
                        }  transition-all duration-300`}
                    />
                  </svg>
                </div>
                <span>{review?.upVoteCount}</span>
              </button>
              <button
                onClick={() => {
                  downVote(review?._id);
                }}
                className="flex group items-baseline gap-4 text-sm text-gray-500 hover:text-gray-700"
              >
                <div className="w-4 h-4">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`${review?.isDownvotedByRequestedUser
                        ? "fill-primary"
                        : "group-hover:fill-primary"
                      }  transition-all duration-300`}
                  >
                    <path
                      d="M14.0245 16.1416L13.8549 15.3341H14.68H21C21.3528 15.3341 21.6911 15.194 21.9406 14.9445C22.19 14.695 22.3302 14.3567 22.3302 14.0039V12.0039C22.3302 11.8369 22.2994 11.6822 22.2398 11.527L19.2243 4.48765L19.2243 4.48767L19.2217 4.48153C19.0235 4.00575 18.5538 3.67372 18 3.67372H9C8.64721 3.67372 8.30887 3.81386 8.05941 4.06332C7.80996 4.31278 7.66981 4.65112 7.66981 5.00391V15.0039C7.66981 15.37 7.81475 15.7014 8.06363 15.9503L14.1722 22.0589L14.7564 21.4803C14.7567 21.48 14.757 21.4797 14.7573 21.4793C14.9067 21.3295 15.0002 21.1181 15.0002 20.8939C15.0002 20.8237 14.9938 20.7592 14.9837 20.7079L14.0245 16.1416ZM4.33019 14.3341V3.67372H1.66981V14.3341H4.33019Z"
                      stroke="black"
                      stroke-opacity="0.4"
                      stroke-width="1.33962"
                      className={`${review?.isDownvotedByRequestedUser
                          ? "stroke-primary"
                          : "group-hover:stroke-primary"
                        } transition-all duration-300`}
                    />
                  </svg>
                </div>
                <span>{review?.downVoteCount}</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {reviewData?.reviews?.length > 6 && (
        <div className="flex justify-center mt-8">
          <button className="px-6 py-2 text-primary border-2 border-primary rounded-md hover:text-white hover:bg-primary transition-all duration-200 font-semibold">
            {dict.poductDescriptionPage.viewMoreReviews}
          </button>
        </div>
      )}
    </div>
  );
}
